import React from "react";
import { useTranslation } from "react-i18next";
import { Modal } from '@amzn/alchemy-components-react';
import { useBulkManageScan2Auth } from "src/hooks/use-bulk-manage-scan2auth";
import { AuthgardCredentialType } from "src/models/bulk-execute-authgard-admin-operation";
import type { BulkUser } from "src/hooks/use-bulk-manage-scan2auth";
import type { ManageScan2AuthStatusChange } from "src/hooks/use-manage-scan2auth";
import { SelectActionStep } from "src/components/modals/bulk-manage-credentials/select-action-step";
import { ConfirmStep } from "src/components/modals/bulk-manage-credentials/confirm-step";
import { ResultsStep } from "src/components/modals/bulk-manage-credentials/results-step";
import { useCloseButtonOnlyDismissal } from "src/components/modals/bulk-manage-credentials/dismiss-guard";

/** Shared by the <Modal> and the dismissal guard, which looks the host up by id. */
const MODAL_ID = "bulk-manage-scan2auth-modal";

interface BulkManageScan2AuthModalProps {
    open: boolean
    setOpen: CallableFunction

    /**
     * The operator's full selection. Principals the other credential type applies to are surfaced as
     * ineligible here rather than filtered out by the caller — so the counts always reconcile with what
     * the operator ticked.
     */
    users: BulkUser[]

    /**
     * The site the selection was made under. Pins the scope for the run.
     */
    scope: string

    /**
     * Pinned by the bulk entry the operator chose, so a run never mixes credential types.
     */
    credentialType: AuthgardCredentialType

    /** Receives each successful run's confirmed status, so the principal list reflects it without a refresh. */
    onStatusChange?: ManageScan2AuthStatusChange
}

/**
 * Bulk Authgard MFA management wizard (Scan2Auth / FIDO2). This component owns only the modal chrome
 * and which step is showing; all wizard state and the provider-backed results flow live in
 * useBulkManageScan2Auth, and each step renders itself (see bulk-manage-credentials/). Every
 * user-facing string routes through i18n, and results come from the real
 * bulkExecuteAuthgardAdminOperation provider (served by the dev mock link in development), so nothing
 * here is fabricated.
 */
export const BulkManageScan2AuthModal = (props: BulkManageScan2AuthModalProps) => {

    const { t } = useTranslation();

    const bulk = useBulkManageScan2Auth({
        open: props.open,
        users: props.users,
        scope: props.scope,
        credentialType: props.credentialType,
        onStatusChange: props.onStatusChange,
    });

    const closeModal = () => {
        bulk.resetState();
        props.setOpen(false);
    };

    // Restrict dismissal to the X only while there is something to lose: a run in flight can't be
    // recalled, and the results carry codes the operator may not have recorded yet. On the earlier
    // steps nothing is at stake, so Escape and a backdrop click work normally — taking Escape away
    // everywhere would cost keyboard users their fastest exit to guard a step that risks nothing.
    useCloseButtonOnlyDismissal(props.open && (bulk.submitting || bulk.step === 'results'), MODAL_ID);

    const renderBody = (): React.ReactNode => {
        switch (bulk.step) {
            case 'select-action': return <SelectActionStep bulk={bulk} />;
            case 'confirm': return <ConfirmStep bulk={bulk} scope={props.scope} />;
            case 'results': return <ResultsStep bulk={bulk} onDone={closeModal} />;
            default: return null;
        }
    };

    return (
        <Modal
            id={MODAL_ID}
            // bulk-wizard-modal raises Alchemy's 80vh content cap; see the rule in index.scss.
            className="mb-1 bulk-wizard-modal"
            // lg is 1200px against the default 800px; the wizard lists people, so it needs the room.
            size="lg"
            header={props.credentialType === 'FIDO2'
                ? t('bulk-manage-credentials-header-fido2')
                : t('bulk-manage-credentials-header-scan2auth')}
            headerDescription={t('bulk-manage-credentials-subtitle', { count: props.users.length })}
            open={props.open}
            onOpen={() => {}}
            onClose={(e: Event) => {
                // Alchemy's <Modal> dispatches a bubbling, composed `close` CustomEvent on the host
                // for every real dismissal (X, overlay, ESC, programmatic open=false). Treat it as a
                // close only when it originated AT the modal host — matches the single-user modal.
                //
                // NOTE: keep this handler free of render-state reads. The React wrapper binds it once
                // in componentDidMount and never re-binds (componentDidUpdate re-syncs attributes
                // only), so the listener is frozen to the first render's closure — the step, results
                // and submitting state read here would be permanently their initial values. Only
                // stable setters (bulk.resetState, props.setOpen) are safe to call.
                if (e.target === e.currentTarget) {
                    closeModal();
                }
            }}
        >
            {/* No stopPropagation on the body: the modal's own outside-click handler already ignores
                clicks whose composedPath includes the content, so inner clicks never dismiss it.

                The width has to be asked for here. Alchemy's modal content sets only a max-width, so
                the dialog shrinks to fit its children — size="lg" raises the ceiling from 800px to
                1200px but changes nothing on its own. The calc keeps it inside the viewport, since the
                content box carries a 2.5rem margin on each side.

                `width` with `maxWidth: 100%`, NOT `minWidth`. The content box scrolls once it hits its
                max-height, so after shrink-wrapping to this width a vertical scrollbar appears and takes
                ~15px out of the space it just allocated. A min-width refuses to give that back —
                min-width wins over max-width in the cascade — and the leftover showed up as a
                horizontal scrollbar across the whole dialog. max-width does override width, so this
                yields to the scrollbar instead. */}
            <div
                className="container-fluid"
                style={{ width: 'min(1040px, calc(100vw - 6rem))', maxWidth: '100%' }}
            >
                {renderBody()}
            </div>
        </Modal>
    );
};
